import { useEffect } from "react";
import mixpanel from "mixpanel-browser";
import { env } from "../config/env";
import type { CurrentUser } from "../domains/user/hooks/useCurrentUser";
import { isJuisciGptOnboarded } from "../tools/analytics/juisciGptOnboarded";

export let mixpanelInitialized = false;

const heldUntilInitialized: Array<() => void> = [];

/**
 * Run `send` right after `useMixpanel` initializes Mixpanel. For the mirror of
 * an event pushed at boot (`base.analytics.ts`), which `mixpanel.track` would
 * otherwise drop. The caller checks `mixpanelInitialized` first.
 */
export function whenMixpanelInitialized(send: () => void): void {
  heldUntilInitialized.push(send);
}

/**
 * Stamp this SDK as the reporter of everything it sends. The counterpart of the
 * `emitter: "server"` stamp the api puts on every event it reports
 * (`apps/api/src/service/mixpanel.ts`). A super property, so it rides on
 * autocaptured events too, which is most of the desktop population.
 *
 * Why it is needed: a device panel breaks down by `$device`, which this SDK
 * fills from the user agent — `iPhone`, `Android`, or nothing at all on a
 * desktop browser. "Nothing at all" is how desktop is read, so anything
 * reported WITHOUT this SDK also read as desktop, and the server's events
 * inflated the desktop count to 116 for the week of 31/08/2026 against 36 real
 * users. `emitter` separates the two populations by one property that is always
 * set, instead of by a list of event names someone has to keep up to date.
 * Filter `emitter is client` for a device panel that stays true as new server
 * events ship.
 *
 * "client" and not "web": this bundle is also what runs inside the iOS and
 * Android webviews, where `$device` is correctly set. The property says where
 * the event was REPORTED FROM, never which device it happened on — that
 * distinction is exactly what `docs/ga4-analytics-streams.md` records GA4's own
 * `platform` field getting wrong.
 *
 * Private on purpose: `mixpanel.reset()` clears super properties, so a caller
 * that resets and forgets to stamp again reports the rest of the SPA session,
 * a re-login included, with no `emitter` at all. That is the same silent
 * undercount the stamp exists to prevent, in the other direction. Resetting
 * therefore goes through `resetMixpanelIdentity` below, which cannot forget.
 */
function registerClientEmitter() {
  mixpanel.register({ emitter: "client" });
}

/**
 * Forget the current reader and stamp the fresh identity, in that order. What
 * logout calls (`domains/user/hooks/useLogOutUser.ts`).
 *
 * The two steps are one function because `mixpanel.reset()` alone leaves the
 * SDK reporting events with no `emitter`, and nothing downstream would say so:
 * the events keep arriving, the device panel just stops counting those readers.
 * A raw `mixpanel.reset()` call anywhere in the webapp is a bug.
 */
export function resetMixpanelIdentity() {
  mixpanel.reset();
  registerClientEmitter();
}

export function useMixpanel(token: string | null) {
  useEffect(() => {
    if (token === null) return;
    if (env.appEnv === "development") return;

    // No `record_sessions_percent`, and that omission IS the setting: the SDK
    // default is 0, so no session is recorded. The DPO stopped session
    // recording on 2026-10-29: it needs the reader's consent, and no consent
    // tool runs yet.
    //
    // If recording comes back, never pass `record_mask_text_selector`, not
    // even `""`. The key flips the SDK's `text.maskAll` to false and unmasks
    // every rendered text node (`recorder/masking.js`). It was passed as `""`
    // from 54f4e41a20 until 2026-09-16, which sent the text of the pages
    // readers browse to Mixpanel: their own name and email on the settings
    // page, and every GPT message once it rendered into the conversation.
    mixpanel.init(token, {
      autocapture: true,
      api_host: "https://api-eu.mixpanel.com",
    });

    // The counterpart of the `emitter: "server"` stamp the api puts on every
    // event it reports (`apps/api/src/service/mixpanel.ts`). Registered as a
    // super property, so it rides on autocaptured events too.
    //
    // Why it is needed: a device panel breaks down by `$device`, which this SDK
    // fills from the user agent — `iPhone`, `Android`, or nothing at all on a
    // desktop browser. "Nothing at all" is how desktop is read, so anything
    // reported WITHOUT this SDK also read as desktop, and the server's events
    // inflated the desktop count to 116 for the week of 31/08/2026 against 36
    // real users. `emitter` separates the two populations by one property that
    // is always set, instead of by a list of event names someone has to keep up
    // to date. Filter `emitter is client` for a device panel that stays true as
    // new server events ship.
    //
    registerClientEmitter();

    mixpanelInitialized = true;
    for (const send of heldUntilInitialized.splice(0)) send();
  }, [token]);
}

export function useMixpanelUser(
  currentUser: CurrentUser | null,
  roomNames: string[] | null
) {
  useEffect(() => {
    if (!currentUser || !roomNames) return;
    if (env.appEnv === "development") return;
    if (!mixpanelInitialized) return;

    // No PII (name, email) — full profile synced server-side by api.
    mixpanel.identify(currentUser.uid);
    mixpanel.people.set({
      profession: currentUser.professionModel?.uid ?? null,
      main_specialty: currentUser.mainSpecialtyModel?.uid ?? null,
      declared_country: currentUser.country ?? null,
      language: currentUser.language,
      total_joined_rooms: roomNames.length,
      rooms_joined: roomNames,
      tags: currentUser.tagModels.map((t) => t.uid),
      medical_specialties: currentUser.medicalSpecialtyModels.map((t) => t.uid),
      has_medical_identifier: currentUser.hcpRegistryIds.length > 0,
    });

    // Remove legacy properties that are no longer in the tracking plan
    mixpanel.people.unset([
      "main_specialty_model",
      "medical_specialty",
      "medical_specialty_models",
      "profession_model",
      "rooms",
    ]);
  }, [currentUser, roomNames]);
}

const JUISCIGPT_ONBOARDING = "juiscigpt_onboarding";

/**
 * What the webapp knows of the reader for `juiscigpt_onboarding`:
 * - `signed_out`: no session, so the events are anonymous;
 * - `unknown`: a session, but the user or their first question is still
 *   loading, or failed to load;
 * - `signed_in`: the time of their first JuisciGPT question, null when they
 *   never asked one (`GET /webapp/private/gpt/first-question`).
 */
export type JuisciGptOnboardingReader =
  | { kind: "signed_out" }
  | { kind: "unknown" }
  | { kind: "signed_in"; firstQuestionAt: string | null };

/**
 * Keep the super property `juiscigpt_onboarding` ("onboarded" or
 * "not_onboarded") on every event of a signed-in reader, and off every
 * anonymous event. Mixpanel charts the two weekly JuisciGPT KPIs from it
 * (`services/analytics/AGENTS.md`). It is a string, not a boolean: a Mixpanel
 * boolean filter "is false" also matches the events that have no value.
 *
 * `unknown` changes nothing: the value that Mixpanel persisted from the last
 * session stays until the first question loads. A session can cross a Monday,
 * so the value is computed again each time the app becomes visible. A first
 * question needs no new fetch: it keeps the value false for its whole week.
 */
export function useMixpanelJuisciGptOnboarded(
  reader: JuisciGptOnboardingReader
) {
  const kind = reader.kind;
  const firstQuestionAt =
    reader.kind === "signed_in" ? reader.firstQuestionAt : null;
  useEffect(() => {
    if (env.appEnv === "development") return;
    if (!mixpanelInitialized) return;
    switch (kind) {
      case "signed_out":
        mixpanel.unregister(JUISCIGPT_ONBOARDING);
        return;
      case "unknown":
        return;
      case "signed_in": {
        const firstQuestion =
          firstQuestionAt === null ? null : new Date(firstQuestionAt);
        const register = () =>
          mixpanel.register({
            [JUISCIGPT_ONBOARDING]: isJuisciGptOnboarded(
              firstQuestion,
              new Date()
            )
              ? "onboarded"
              : "not_onboarded",
          });
        register();
        const onVisibilityChange = () => {
          if (document.visibilityState === "visible") register();
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () =>
          document.removeEventListener("visibilitychange", onVisibilityChange);
      }
      default:
        throw new Error(kind satisfies never);
    }
  }, [kind, firstQuestionAt]);
}
